import { initializeApp, getApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  addDoc,
  orderBy,
  deleteDoc,
  Firestore
} from 'firebase/firestore';

import {
  College,
  Assessment,
  Question,
  CandidateAttempt,
  AttemptStatus,
  UserRole,
  SystemSettings,
  AssessmentLink
} from '../types';

/* =========================
   Firebase Configuration
========================= */
const firebaseConfig = {
  apiKey: "AIzaSyAxAlZSooBfROhqIsSNuzuF5sZk-NSWkSE",
  authDomain: "task-9600.firebaseapp.com",
  projectId: "task-9600",
  storageBucket: "task-9600.appspot.com",
  messagingSenderId: "175303172834",
  appId: "1:175303172834:web:859b51e29bb910d3f674bb"
};

let db: Firestore | null = null;

const initFirebase = (): Firestore | null => {
  if (db) return db;

  try {
    const app: FirebaseApp =
      getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

    db = getFirestore(app);
    return db;
  } catch (error) {
    console.error("Firebase initialization failed:", error);
    return null;
  }
};

/* =========================
   Storage Service
========================= */
export class StorageService {

  private static getDB(): Firestore {
    const firestore = initFirebase();
    if (!firestore) {
      throw new Error("Firestore service not available");
    }
    return firestore;
  }

  /* ---------- SETTINGS ---------- */

  static async getSettings(): Promise<SystemSettings> {
    try {
      const db = this.getDB();
      const ref = doc(db, 'settings', 'global');
      const snap = await getDoc(ref);

      if (snap.exists()) {
        return snap.data() as SystemSettings;
      }

      const defaults: SystemSettings = {
        recruitAccessPin: 'admin123'
      };

      await setDoc(ref, defaults, { merge: true });
      return defaults;

    } catch (e) {
      console.warn("Using fallback settings:", e);
      return { recruitAccessPin: 'admin123' };
    }
  }

  static async updateSettings(settings: SystemSettings) {
    try {
      const db = this.getDB();
      const ref = doc(db, 'settings', 'global');
      await setDoc(ref, settings, { merge: true });
    } catch (e) {
      console.error("Failed to update settings:", e);
    }
  }

  /* ---------- AUTH (LOCAL) ---------- */

  static async loginAdmin(pin: string): Promise<boolean> {
    const settings = await this.getSettings();
    if (pin === settings.recruitAccessPin) {
      localStorage.setItem(
        'magil_session',
        JSON.stringify({ role: UserRole.ADMIN, name: 'Admin' })
      );
      return true;
    }
    return false;
  }

  static logout() {
    localStorage.removeItem('magil_session');
  }

  static getCurrentUser() {
    const session = localStorage.getItem('magil_session');
    return session ? JSON.parse(session) : null;
  }

  /* ---------- COLLEGES ---------- */

  static async getColleges(): Promise<College[]> {
    try {
      const db = this.getDB();
      const snap = await getDocs(collection(db, 'colleges'));
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as College));
    } catch (e) {
      console.error("Failed to fetch colleges:", e);
      return [];
    }
  }

  static async saveCollege(college: College) {
    try {
      const db = this.getDB();
      await setDoc(doc(db, 'colleges', college.id), college, { merge: true });
    } catch (e) {
      console.error("Failed to save college:", e);
    }
  }

  static async deleteCollege(id: string) {
    try {
      const db = this.getDB();
      await deleteDoc(doc(db, 'colleges', id));
    } catch (e) {
      console.error("Failed to delete college:", e);
    }
  }

  /* ---------- ASSESSMENTS ---------- */

  static async getAssessments(): Promise<Assessment[]> {
    try {
      const db = this.getDB();
      const snap = await getDocs(collection(db, 'assessments'));
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as Assessment));
    } catch (e) {
      console.error("Failed to fetch assessments:", e);
      return [];
    }
  }

  static async saveAssessment(assessment: Assessment) {
    try {
      const db = this.getDB();
      await setDoc(doc(db, 'assessments', assessment.id), assessment, { merge: true });
    } catch (e) {
      console.error("Failed to save assessment:", e);
    }
  }

  static async deleteAssessment(id: string) {
    try {
      const db = this.getDB();
      // First delete associated questions
      const qSnap = await getDocs(query(collection(db, 'questions'), where('assessmentId', '==', id)));
      qSnap.forEach(async (d) => await deleteDoc(doc(db, 'questions', d.id)));

      // Delete assessment
      await deleteDoc(doc(db, 'assessments', id));
    } catch (e) {
      console.error("Failed to delete assessment:", e);
    }
  }

  /* ---------- QUESTIONS ---------- */

  static async getQuestions(assessmentId?: string): Promise<Question[]> {
    try {
      const db = this.getDB();
      const ref = collection(db, 'questions');

      const q = assessmentId
        ? query(ref, where('assessmentId', '==', assessmentId))
        : ref;

      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as Question));
    } catch (e) {
      console.error("Failed to fetch questions:", e);
      return [];
    }
  }

  static async saveQuestion(question: Question) {
    try {
      const db = this.getDB();
      await setDoc(doc(db, 'questions', question.id), question, { merge: true });
    } catch (e) {
      console.error("Failed to save question:", e);
    }
  }

  static async deleteQuestion(id: string) {
    try {
      const db = this.getDB();
      await deleteDoc(doc(db, 'questions', id));
    } catch (e) {
      console.error("Failed to delete question:", e);
    }
  }

  /* ---------- ATTEMPTS ---------- */

  static async createAttempt(
    candidateName: string,
    department: string | undefined,
    studentId: string,
    collegeId: string,
    assessmentId: string
  ): Promise<CandidateAttempt> {

    const db = this.getDB();
    const attemptsRef = collection(db, 'attempts');

    const existing = await getDocs(
      query(
        attemptsRef,
        where('studentId', '==', studentId),
        where('assessmentId', '==', assessmentId)
      )
    );

    if (!existing.empty) {
      throw new Error('You have already attempted this assessment.');
    }

    const questions = await this.getQuestions(assessmentId);
    const assessmentSnap = await getDoc(doc(db, 'assessments', assessmentId));
    const assessment = assessmentSnap.data() as Assessment;

    // Lookup college name for snapshotting
    const collegeSnap = await getDoc(doc(db, 'colleges', collegeId));
    const collegeData = collegeSnap.data() as College;
    const collegeName = collegeData?.name || 'Unknown';

    // START Stratified Sampling Logic
    const requestedCount = (assessment && assessment.questionCount > 0) ? assessment.questionCount : questions.length;

    // 1. Group by category
    const questionsByCategory: Record<string, Question[]> = {};
    questions.forEach(q => {
      const cat = q.category || 'General';
      if (!questionsByCategory[cat]) questionsByCategory[cat] = [];
      questionsByCategory[cat].push(q);
    });

    const categories = Object.keys(questionsByCategory);
    let finalSelection: Question[] = [];

    if (categories.length > 0) {
      // 2. Calculate quota per category
      const perCategoryQuota = Math.floor(requestedCount / categories.length);
      const usedQuestionIds = new Set<string>();

      // 3. Pick from each category
      categories.forEach(cat => {
        // Shuffle this category's questions
        const catQs = questionsByCategory[cat].sort(() => Math.random() - 0.5);
        // Take up to quota
        const activeSlice = catQs.slice(0, perCategoryQuota);
        activeSlice.forEach(q => {
          finalSelection.push(q);
          usedQuestionIds.add(q.id);
        });
      });

      // 4. Fill remaining slots (due to floor division or shortages)
      let remainingNeeded = requestedCount - finalSelection.length;
      if (remainingNeeded > 0) {
        // Get all unused questions
        const remainingPool = questions
          .filter(q => !usedQuestionIds.has(q.id))
          .sort(() => Math.random() - 0.5);

        finalSelection.push(...remainingPool.slice(0, remainingNeeded));
      }
    } else {
      // Fallback if no questions exist (edge case)
      finalSelection = questions.sort(() => Math.random() - 0.5).slice(0, requestedCount);
    }

    // 5. Final Shuffle and ID extraction
    let questionOrder = finalSelection
      .map(q => q.id)
      .sort(() => Math.random() - 0.5);
    // END Stratified Sampling Logic

    const attemptData: Omit<CandidateAttempt, 'id'> = {
      candidateName,
      department,
      studentId,
      collegeId,
      collegeName,
      assessmentId,
      startTime: Date.now(),
      status: AttemptStatus.IN_PROGRESS,
      responses: {},
      violations: 0,
      auditLogs: [
        { id: 'l1', timestamp: Date.now(), type: 'START', details: 'Assessment started' }
      ],
      questionOrder
    };

    const ref = await addDoc(attemptsRef, attemptData);
    const attempt = { id: ref.id, ...attemptData };

    localStorage.setItem(
      'magil_session',
      JSON.stringify({ role: UserRole.CANDIDATE, id: ref.id, name: candidateName, department })
    );

    return attempt;
  }

  static async getAttempt(id: string): Promise<CandidateAttempt | undefined> {
    try {
      const db = this.getDB();
      const snap = await getDoc(doc(db, 'attempts', id));
      return snap.exists() ? ({ id: snap.id, ...snap.data() } as CandidateAttempt) : undefined;
    } catch (e) {
      console.error("Failed to fetch attempt:", e);
      return undefined;
    }
  }

  static async updateAttempt(attempt: CandidateAttempt) {
    try {
      const db = this.getDB();
      const { id, ...data } = attempt;
      if (!id) {
        console.error("updateAttempt failed: No ID provided", attempt);
        return;
      }
      // console.log("Updating attempt:", id, Object.keys(data)); 
      // if (attempt.lastSnapshot) console.log("Writing snapshot (length):", attempt.lastSnapshot.length);

      await updateDoc(doc(db, 'attempts', id), data);
    } catch (e) {
      console.error("Failed to update attempt:", e);
    }
  }

  static async getAllAttempts(): Promise<CandidateAttempt[]> {
    try {
      const db = this.getDB();
      const snap = await getDocs(
        query(collection(db, 'attempts'), orderBy('startTime', 'desc'))
      );
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as CandidateAttempt));
    } catch (e) {
      console.error("Failed to fetch attempts:", e);
      return [];
    }
  }

  static async deleteAttempt(id: string) {
    try {
      const db = this.getDB();
      await deleteDoc(doc(db, 'attempts', id));
    } catch (e) {
      console.error("Failed to delete attempt:", e);
    }
  }

  /* ---------- ASSESSMENT LINKS ---------- */

  static async createAccessLink(id: string, collegeId: string, assessmentId: string) {
    const db = this.getDB();
    const docRef = doc(db, 'access_links', id);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      throw new Error("Link ID already exists. Please choose another.");
    }

    await setDoc(docRef, {
      id,
      collegeId,
      assessmentId,
      createdAt: Date.now()
    });
  }

  static async getAccessLinks(): Promise<AssessmentLink[]> {
    try {
      const db = this.getDB();
      const snap = await getDocs(query(collection(db, 'access_links'), orderBy('createdAt', 'desc')));
      return snap.docs.map(d => d.data() as AssessmentLink);
    } catch (e) {
      console.error("Failed to fetch links:", e);
      return [];
    }
  }

  static async deleteAccessLink(id: string) {
    try {
      const db = this.getDB();
      await deleteDoc(doc(db, 'access_links', id));
    } catch (e) {
      console.error("Failed to delete link:", e);
    }
  }

  static async getAccessLink(id: string): Promise<AssessmentLink | null> {
    try {
      const db = this.getDB();
      const snap = await getDoc(doc(db, 'access_links', id));
      return snap.exists() ? (snap.data() as AssessmentLink) : null;
    } catch (e) {
      console.error("Failed to get link:", e);
      return null;
    }
  }
}
